import React, {forwardRef, ReactNode, useImperativeHandle, useState} from 'react';
import {Alert, Avatar, CircularProgress, LinearProgress, List, ListItem, ListItemAvatar, ListItemText} from "@mui/material";
import {uploadFile} from "../../backend/FileUploadApi";
import {getFileBase64} from "../../utils/ReadFile";
import {logEvent} from "../../utils/SentryUtils";
import {Trans, Translation} from "react-i18next";
import FolderIcon from "@mui/icons-material/Folder";

export interface FileUploadProgressProps {
    onUploadFinish?: () => void
}

export interface FileToUpload {
    alias?: string
    file: File
}

export interface FileUploadProgressHandle {
    doUpload: (sessionId: string, files: FileToUpload[]) => Promise<boolean>;
}

const FileUploadProgress: React.ForwardRefRenderFunction<FileUploadProgressHandle, FileUploadProgressProps> = ({onUploadFinish}, forwardedRef,) => {
    const [uploadFinished, setUploadFinished] = useState(false);
    const [uploadingFile, setUploadingFile] = useState("");
    const [progress, setProgress] = useState(0);

    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<ReactNode>('');


    useImperativeHandle(forwardedRef, () => ({
        async doUpload(sessionId, files) {
            for (const fileWrapper of files) {
                const index = files.indexOf(fileWrapper);
                const fileName = fileWrapper.alias ?? fileWrapper.file.name;

                setUploadFinished(false);
                setUploadingFile(fileName);
                setProgress(((index + 1) / files.length * 100))

                try {
                    await uploadFile(sessionId, fileName, await getFileBase64(fileWrapper.file))
                    logEvent("file_upload", `File ${fileName} upload for session ${sessionId}`);
                } catch (e) {
                    logEvent("file_upload", `Failed to upload file ${fileName} for session ${sessionId}, error is ${e}`, 'fatal');
                    console.error(e);

                    setUploadFinished(true);
                    setShowError(true)
                    setErrorMessage(<><Translation>{t => t("documents.upload.upload.error", "Failed to upload file, please contact support!")}</Translation> ({sessionId})</>)
                    onUploadFinish?.();

                    return false;
                }
            }
            setUploadFinished(true);
            onUploadFinish?.();
            return true;
        }
    }));

    return <>
        <LinearProgress variant="determinate" value={progress}/>
        {showError && <Alert severity="error">{errorMessage}</Alert>}
        {(!uploadFinished || showError) &&
            <Alert icon={<CircularProgress style={{alignSelf: "center"}}/>} severity="success">
                <List dense>
                    <ListItem>
                        <ListItemAvatar>
                            <Avatar>
                                <FolderIcon/>
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={uploadingFile}/>
                    </ListItem>
                </List>
            </Alert>}
        {uploadFinished && !showError && <Alert severity="success">
            <Trans i18nKey="documents.upload.upload.success">All files were uploaded!</Trans>
        </Alert>}
    </>;
}

export default forwardRef(FileUploadProgress);